export const M9_UNIT_8 = {
  id: "m9-u8-passive-process",
  title: "Unidad 8: Describing Processes with Passive Voice",
  topic: "Master scientific descriptions! Learn to describe experiments and processes naturally using passive voice. Essential for lab work, instructions, and professional contexts!",
  iconType: "flask",
  grammarImage: "/grammar/grammar-m9-u8.jpg",
  grammarText: "Use PASSIVE VOICE when the action matters more than who does it. Pattern: Subject + IS/ARE + PAST PARTICIPLE. Perfect for scientific processes, recipes, instructions!",
  
  sets: [
    // SET 1
    {
      id: "m9-u8-s1",
      title: "Set 1: Passive Voice Basics - Recognition 🔬",
      context: `**Science Class Introduction**

Teacher: "Welcome to the lab! Today we'll learn how scientists describe experiments. Let's start with some simple processes..."`,
      
      questions: [
        {
          type: "mc",
          q: "Which sentence focuses on the ACTION, not the person?",
          options: [
            "The water is heated to 100°C",
            "The scientist heats the water",
            "We heat the water carefully"
          ],
          a: "The water is heated to 100°C",
          tip: "Passive voice puts the thing receiving the action first!",
          successtip: "✓ PASSIVE RECOGNITION!\nIn labs worldwide, scientists use passive voice to focus on WHAT happens, not WHO does it! 🔬"
        },
        {
          type: "match",
          q: "Match the active sentence with its passive version:",
          matches: [
            { left: "We heat the liquid", right: "The liquid is heated" },
            { left: "They mix the chemicals", right: "The chemicals are mixed" },
            { left: "She records the results", right: "The results are recorded" }
          ],
          successtip: "✓ ACTIVE TO PASSIVE!\nBoth say the same thing, but passive sounds more professional in scientific contexts! 📊"
        },
        {
          type: "mc",
          q: "What is the correct passive form?",
          options: [
            "The experiment is observe carefully",
            "The experiment is observed carefully",
            "The experiment observes carefully"
          ],
          a: "The experiment is observed carefully",
          tip: "Pattern: IS/ARE + PAST PARTICIPLE (observed, not observe)!",
          successtip: "✓ PAST PARTICIPLE!\nRemember: IS/ARE + verb in past participle form! This works for all passive sentences! ✨"
        },
        {
          type: "input",
          q: "Complete: 'The temperature _____ every 5 minutes.' (take)",
          a: "is taken",
          alts: ["gets taken"],
          tip: "Use IS + past participle of 'take'!",
          successtip: "✓ IS TAKEN!\nSingular subjects use IS + past participle. Plural subjects use ARE + past participle! 📝"
        },
        {
          type: "traduce",
          q: "Traduce: 'El agua se calienta'",
          words: ["is", "The", "heated", "water"],
          a: "The water is heated",
          extra: ["was", "are"],
          tip: "Start with the thing being heated!",
          successtip: "✓ NATURAL PASSIVE!\nIn English, we often use passive for processes that 'happen' to things! 🌡️"
        },
        {
          type: "mc",
          q: "Which is the passive sentence?",
          options: [
            "The scientist pours the liquid",
            "The liquid is poured into the beaker",
            "We pour the liquid slowly"
          ],
          a: "The liquid is poured into the beaker",
          successtip: "✓ PROCESS DESCRIPTION!\nPassive is perfect for step-by-step instructions and scientific procedures! 🧪"
        },
        {
          type: "input",
          q: "Complete: 'The chemicals _____ together.' (mix - plural!)",
          a: "are mixed",
          alts: ["get mixed"],
          tip: "Plural subject = ARE + past participle!",
          successtip: "✓ ARE MIXED!\nPlural subjects always use ARE in present passive! ARE heated, ARE observed, ARE recorded! 🔄"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los resultados se registran'",
          words: ["results", "are", "The", "recorded"],
          a: "The results are recorded",
          extra: ["is", "record"],
          successtip: "✓ PROFESSIONAL STYLE!\nThis pattern is used in science labs, research papers, and professional reports worldwide! 📄"
        },
        {
          type: "match",
          q: "Match the verb with its past participle:",
          matches: [
            { left: "heat", right: "heated" },
            { left: "observe", right: "observed" },
            { left: "take", right: "taken" }
          ],
          tip: "Regular verbs add -ED, but some are irregular!",
          successtip: "✓ PARTICIPLES!\nMost science verbs are regular (-ED), but watch for irregulars like TAKE → TAKEN! 💡"
        },
        {
          type: "mc",
          q: "Complete: 'The solution _____ in a beaker.'",
          options: [
            "is place",
            "is placed",
            "are placed"
          ],
          a: "is placed",
          tip: "Singular subject needs IS + past participle!",
          successtip: "✓ IS PLACED!\nSingular subjects (the solution, the liquid, the sample) always use IS! 🧪"
        }
      ]
    },

    // SET 2
    {
      id: "m9-u8-s2",
      title: "Set 2: Lab Equipment & Simple Processes 🧪",
      context: `**First Lab Experiment**

Lab Assistant: "Here's your equipment: beakers, thermometer, and safety goggles. Let me show you how we describe what happens in the lab..."`,
      
      questions: [
        {
          type: "mc",
          q: "What equipment do you use to measure temperature?",
          options: [
            "A thermometer",
            "A beaker",
            "A microscope"
          ],
          a: "A thermometer",
          successtip: "✓ LAB EQUIPMENT!\nThermometers are used in labs worldwide! From Celsius to Fahrenheit, same tool! 🌡️"
        },
        {
          type: "traduce",
          q: "Traduce: 'El termómetro se coloca en el agua'",
          words: ["thermometer", "is", "in", "The", "placed", "water", "the"],
          a: "The thermometer is placed in the water",
          extra: ["are"],
          tip: "Pattern: The [equipment] + IS PLACED + location!",
          successtip: "✓ EQUIPMENT PLACEMENT!\nThis pattern works for any lab equipment: 'is placed', 'is inserted', 'is positioned'! 🔬"
        },
        {
          type: "input",
          q: "Complete: 'Safety goggles _____ during experiments.' (wear)",
          a: "are worn",
          alts: ["must be worn"],
          tip: "Plural subject + irregular past participle (wear → worn)!",
          successtip: "✓ SAFETY FIRST!\nWEAR → WORN is irregular! Always use passive for safety rules in labs! 🥽"
        },
        {
          type: "match",
          q: "Match the equipment with its use:",
          matches: [
            { left: "Beaker", right: "To hold liquids" },
            { left: "Thermometer", right: "To measure temperature" },
            { left: "Microscope", right: "To observe small things" }
          ],
          successtip: "✓ LAB ESSENTIALS!\nThese tools are universal in science labs - same names in US, UK, everywhere! 🌍"
        },
        {
          type: "mc",
          q: "Complete: 'The liquid _____ into a beaker.'",
          options: [
            "pours",
            "is poured",
            "are poured"
          ],
          a: "is poured",
          tip: "The liquid (singular) receives the action!",
          successtip: "✓ IS POURED!\nPOUR is a key science verb! Also: 'is heated', 'is stirred', 'is mixed'! 🧪"
        },
        {
          type: "traduce",
          q: "Traduce: 'La mezcla se observa cuidadosamente'",
          words: ["carefully", "is", "observed", "mixture", "The"],
          a: "The mixture is observed carefully",
          extra: ["are", "observes"],
          successtip: "✓ CAREFUL OBSERVATION!\nAdverbs like 'carefully' usually go at the END in passive sentences! 👀"
        },
        {
          type: "input",
          q: "Complete: 'The water _____ until it boils.' (heat)",
          a: "is heated",
          tip: "Present passive: IS + HEATED!",
          successtip: "✓ HEATING PROCESS!\n'Until it boils' = hasta que hierva. Perfect combo with passive voice! 🔥"
        },
        {
          type: "match",
          q: "Match the process with the correct passive form:",
          matches: [
            { left: "To pour a liquid", right: "The liquid is poured" },
            { left: "To stir a mixture", right: "The mixture is stirred" },
            { left: "To cool a substance", right: "The substance is cooled" }
          ],
          tip: "All use IS + past participle!",
          successtip: "✓ PROCESS VERBS!\nThese are the most common actions in any lab experiment! Master these! 💪"
        },
        {
          type: "mc",
          q: "What happens when you heat ice?",
          options: [
            "It is frozen",
            "It is melted",
            "It is boiled"
          ],
          a: "It is melted",
          successtip: "✓ MELTING!\nICE → MELTS → IS MELTED. Water → BOILS → IS BOILED. Different processes! ❄️➡️💧"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los químicos se añaden lentamente'",
          words: ["slowly", "are", "chemicals", "added", "The"],
          a: "The chemicals are added slowly",
          extra: ["is", "add"],
          tip: "Plural = ARE + past participle!",
          successtip: "✓ PLURAL PASSIVE!\nChemicals, results, samples (plural) → ARE + past participle! 🧪🧪🧪"
        }
      ]
    },

    // SET 3
    {
      id: "m9-u8-s3",
      title: "Set 3: Temperature & Changes 🌡️",
      context: `**Heat Experiment**

Instructor: "Today we study temperature changes. Watch what happens when substances are heated or cooled..."`,
      
      questions: [
        {
          type: "mc",
          q: "When water is heated to 100°C, what happens?",
          options: [
            "It freezes",
            "It boils",
            "It melts"
          ],
          a: "It boils",
          successtip: "✓ BOILING POINT!\n100°C = 212°F = boiling point worldwide! Science uses the same facts everywhere! 🌡️"
        },
        {
          type: "traduce",
          q: "Traduce: 'La temperatura se mide cada minuto'",
          words: ["is", "temperature", "measured", "The", "minute", "every"],
          a: "The temperature is measured every minute",
          extra: ["are"],
          tip: "'Every minute' goes at the end!",
          successtip: "✓ TIME EXPRESSIONS!\n'Every minute', 'every hour', 'every day' - always at the end of passive sentences! ⏱️"
        },
        {
          type: "input",
          q: "Complete: 'The ice _____ when it is heated.' (melt)",
          a: "is melted",
          alts: ["melts"],
          tip: "Use passive: IS + MELTED!",
          successtip: "✓ MELTING!\nBoth active 'melts' and passive 'is melted' work here! Passive sounds more scientific! 🧊"
        },
        {
          type: "match",
          q: "Match the temperature change with what happens:",
          matches: [
            { left: "Water at 100°C", right: "It boils" },
            { left: "Water at 0°C", right: "It freezes" },
            { left: "Ice above 0°C", right: "It melts" }
          ],
          successtip: "✓ PHASE CHANGES!\nThese temperatures are scientific facts - true in any country, any lab! ❄️💧💨"
        },
        {
          type: "mc",
          q: "Complete: 'Steam _____ when it cools down.'",
          options: [
            "is freeze",
            "is condensed",
            "is boiled"
          ],
          a: "is condensed",
          tip: "Steam (gas) becomes liquid when cooled!",
          successtip: "✓ CONDENSATION!\nGas → Liquid = condensation! CONDENSE is a high-level science term! 💨➡️💧"
        },
        {
          type: "traduce",
          q: "Traduce: 'El agua se congela a 0 grados'",
          words: ["Water", "frozen", "0", "at", "is", "degrees"],
          a: "Water is frozen at 0 degrees",
          extra: ["are", "freeze"],
          successtip: "✓ FREEZING POINT!\n0°C = 32°F = freezing point! Universal scientific fact! 🧊"
        },
        {
          type: "input",
          q: "Complete: 'The liquid _____ to room temperature.' (cool)",
          a: "is cooled",
          alts: ["cools"],
          tip: "Passive: IS + past participle!",
          successtip: "✓ COOLING!\n'Room temperature' = temperatura ambiente. Common phrase in lab procedures! 🌡️"
        },
        {
          type: "match",
          q: "Match the verb with its scientific meaning:",
          matches: [
            { left: "To boil", right: "Liquid becomes gas (100°C)" },
            { left: "To freeze", right: "Liquid becomes solid (0°C)" },
            { left: "To melt", right: "Solid becomes liquid" }
          ],
          successtip: "✓ STATE CHANGES!\nSolid ↔️ Liquid ↔️ Gas. These transformations are universal in science! 🔬"
        },
        {
          type: "mc",
          q: "What form is 'heated' in 'The water is heated'?",
          options: [
            "Present tense",
            "Past participle",
            "Gerund"
          ],
          a: "Past participle",
          tip: "It's the form used after IS/ARE in passive!",
          successtip: "✓ PAST PARTICIPLE!\nHEAT → HEATED (past participle). Don't confuse with past tense - same form for regular verbs! 📚"
        },
        {
          type: "traduce",
          q: "Traduce: 'El vapor se produce cuando hierve'",
          words: ["when", "boils", "Steam", "it", "produced", "is"],
          a: "Steam is produced when it boils",
          extra: ["are"],
          tip: "Steam (singular) needs IS!",
          successtip: "✓ STEAM PRODUCTION!\nSteam = vapor. When water boils, steam is produced! Classic lab observation! 💨"
        }
      ]
    },

    // SET 4
    {
      id: "m9-u8-s4",
      title: "Set 4: Recording Results 📊",
      context: `**Data Collection**

Researcher: "After each experiment, results are recorded on charts. Let me show you proper lab documentation..."`,
      
      questions: [
        {
          type: "mc",
          q: "Where are experiment results typically written?",
          options: [
            "On a chart or table",
            "On the wall",
            "On the equipment"
          ],
          a: "On a chart or table",
          successtip: "✓ DATA RECORDING!\nCharts, tables, graphs - universal ways scientists document results worldwide! 📊"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los resultados se registran en una tabla'",
          words: ["recorded", "The", "a", "on", "results", "are", "chart"],
          a: "The results are recorded on a chart",
          extra: ["is"],
          tip: "Plural results = ARE!",
          successtip: "✓ RECORDING DATA!\n'On a chart', 'in a table', 'on a graph' - common phrases in science! 📈"
        },
        {
          type: "input",
          q: "Complete: 'The data _____ carefully before the conclusion.' (analyze)",
          a: "is analyzed",
          alts: ["are analyzed"],
          tip: "Data can be singular OR plural!",
          successtip: "✓ DATA!\nIn American English, 'data' is often singular. In British English, often plural! Both correct! 🌍"
        },
        {
          type: "match",
          q: "Match the step with its passive form:",
          matches: [
            { left: "Write the results", right: "The results are written" },
            { left: "Record the data", right: "The data is recorded" },
            { left: "Analyze the findings", right: "The findings are analyzed" }
          ],
          successtip: "✓ RESEARCH PROCESS!\nWrite, record, analyze - key steps in any scientific experiment! 🔬"
        },
        {
          type: "mc",
          q: "Complete: 'The temperature _____ every 5 minutes.'",
          options: [
            "is checking",
            "is checked",
            "checks"
          ],
          a: "is checked",
          tip: "Passive = IS + past participle (checked)!",
          successtip: "✓ REGULAR CHECKS!\n'Every X minutes/hours' is common in experiments for consistent measurements! ⏱️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Las observaciones se anotan inmediatamente'",
          words: ["noted", "The", "are", "immediately", "observations"],
          a: "The observations are noted immediately",
          extra: ["is", "note"],
          successtip: "✓ IMMEDIATE RECORDING!\n'Immediately', 'at once', 'right away' - timing words work great with passive! ⚡"
        },
        {
          type: "input",
          q: "Complete: 'The measurements _____ in the laboratory notebook.' (write down)",
          a: "are written down",
          alts: ["are recorded"],
          tip: "Plural + phrasal verb = ARE + WRITTEN DOWN!",
          successtip: "✓ PHRASAL VERB PASSIVE!\nWrite down → are written down. Phrasal verbs work in passive too! 📝"
        },
        {
          type: "match",
          q: "Match the action with the timing:",
          matches: [
            { left: "Record data", right: "Immediately after observation" },
            { left: "Analyze results", right: "After all data is collected" },
            { left: "Write conclusion", right: "After analysis is complete" }
          ],
          successtip: "✓ SCIENTIFIC METHOD!\nObserve → Record → Analyze → Conclude. Standard process worldwide! 🌍"
        },
        {
          type: "mc",
          q: "What does 'The findings are reviewed' mean?",
          options: [
            "Someone looks at the findings again",
            "The findings look at something",
            "The findings are new"
          ],
          a: "Someone looks at the findings again",
          tip: "REVIEW = examine again!",
          successtip: "✓ PEER REVIEW!\nScientific findings are always reviewed by other experts before publication! 👥"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los datos se examinan cuidadosamente'",
          words: ["examined", "carefully", "are", "data", "The"],
          a: "The data are examined carefully",
          extra: ["is"],
          tip: "Use plural ARE if treating data as plural!",
          successtip: "✓ CAREFUL ANALYSIS!\n'Examined carefully', 'analyzed thoroughly' - adverbs add precision to scientific writing! 🔍"
        }
      ]
    },

    // SET 5
    {
      id: "m9-u8-s5",
      title: "Set 5: Complete Experiment Process 🔬",
      context: `**Step-by-Step Procedure**

Lab Manual: "Follow these steps exactly. Each action must be done in order for accurate results..."`,
      
      questions: [
        {
          type: "mc",
          q: "First step: 'The beaker _____ with water.'",
          options: [
            "fills",
            "is filled",
            "are filled"
          ],
          a: "is filled",
          tip: "Singular beaker = IS + FILLED!",
          successtip: "✓ FILLED WITH!\n'Filled with' is super common: filled with water, filled with solution, filled with liquid! 🧪"
        },
        {
          type: "traduce",
          q: "Traduce: 'El vaso se llena hasta la marca de 100ml'",
          words: ["beaker", "to", "filled", "The", "is", "mark", "100ml", "the"],
          a: "The beaker is filled to the 100ml mark",
          extra: ["are"],
          tip: "'To the mark' = hasta la marca!",
          successtip: "✓ PRECISE MEASUREMENTS!\nScience needs precision! 'To the mark', 'to the line', 'exactly 100ml'! 📏"
        },
        {
          type: "input",
          q: "Complete: 'Next, the thermometer _____ into the water.' (place)",
          a: "is placed",
          alts: ["is put", "is inserted"],
          successtip: "✓ POSITIONING!\n'Placed', 'put', 'inserted' - all work for putting equipment in position! 🌡️"
        },
        {
          type: "match",
          q: "Match the experiment steps in order:",
          matches: [
            { left: "Step 1", right: "The beaker is filled with water" },
            { left: "Step 2", right: "The thermometer is placed inside" },
            { left: "Step 3", right: "The water is heated until it boils" }
          ],
          successtip: "✓ SEQUENTIAL STEPS!\nExperiments follow strict sequences. Each step depends on the previous one! 📝"
        },
        {
          type: "mc",
          q: "Complete: 'The mixture _____ for 5 minutes.'",
          options: [
            "is stirred",
            "stir",
            "are stirred"
          ],
          a: "is stirred",
          tip: "Singular mixture = IS + past participle!",
          successtip: "✓ STIRRING!\nSTIR = remover/mezclar. 'Is stirred for X minutes' is very common in procedures! 🥄"
        },
        {
          type: "traduce",
          q: "Traduce: 'La solución se calienta lentamente'",
          words: ["heated", "The", "slowly", "solution", "is"],
          a: "The solution is heated slowly",
          extra: ["are", "heats"],
          tip: "Adverb 'slowly' goes at the end!",
          successtip: "✓ CONTROLLED HEATING!\n'Slowly', 'gradually', 'carefully' - control is essential in experiments! 🔥"
        },
        {
          type: "input",
          q: "Complete: 'The temperature _____ and recorded every minute.' (check)",
          a: "is checked",
          tip: "Use passive for both actions!",
          successtip: "✓ PARALLEL ACTIONS!\n'Is checked AND recorded' - you can combine passive verbs with AND! ✅"
        },
        {
          type: "match",
          q: "Match the substance with what happens to it:",
          matches: [
            { left: "Salt in water", right: "It is dissolved" },
            { left: "Water at 100°C", right: "It is boiled" },
            { left: "Ice above 0°C", right: "It is melted" }
          ],
          successtip: "✓ TRANSFORMATIONS!\nDissolved, boiled, melted - past participles describing complete changes! 🔄"
        },
        {
          type: "mc",
          q: "Final step: 'The results _____ in the notebook.'",
          options: [
            "is written",
            "writes",
            "are written"
          ],
          a: "are written",
          tip: "Plural results = ARE!",
          successtip: "✓ DOCUMENTATION!\nEvery experiment ends with recording results. No documentation = no science! 📓"
        },
        {
          type: "traduce",
          q: "Traduce: 'El experimento se repite tres veces'",
          words: ["experiment", "The", "repeated", "is", "times", "three"],
          a: "The experiment is repeated three times",
          extra: ["are"],
          successtip: "✓ REPETITION!\nGood science requires repetition! 'Is repeated', 'is done again', 'is performed multiple times'! 🔁"
        }
      ]
    },

    // SET 6
    {
      id: "m9-u8-s6",
      title: "Set 6: Negative Passive & Restrictions ⚠️",
      context: `**Safety Rules**

Safety Officer: "In the lab, some things must NOT be done. Let me show you what is prohibited..."`,
      
      questions: [
        {
          type: "mc",
          q: "How do you make passive negative?",
          options: [
            "Add 'not' after is/are",
            "Add 'not' before the verb",
            "Add 'don't' before is/are"
          ],
          a: "Add 'not' after is/are",
          tip: "Pattern: IS/ARE + NOT + past participle!",
          successtip: "✓ NEGATIVE PASSIVE!\nIS NOT heated, ARE NOT mixed. 'Not' sits right after IS/ARE! 🚫"
        },
        {
          type: "traduce",
          q: "Traduce: 'El agua no se agita durante el proceso'",
          words: ["not", "The", "stirred", "water", "the", "during", "is", "process"],
          a: "The water is not stirred during the process",
          extra: ["are"],
          successtip: "✓ NO STIRRING!\n'During the process' = durante el proceso. Great time expression! ⏱️"
        },
        {
          type: "input",
          q: "Complete: 'The mixture _____ directly.' (heat - negative!)",
          a: "is not heated",
          alts: ["isn't heated"],
          tip: "Use IS NOT + past participle!",
          successtip: "✓ INDIRECT HEATING!\nSometimes substances aren't heated directly - they're heated in a water bath! 🔥"
        },
        {
          type: "match",
          q: "Match the safety rule with its meaning:",
          matches: [
            { left: "Food is not allowed", right: "Don't bring food to lab" },
            { left: "Chemicals are not touched", right: "Don't touch with bare hands" },
            { left: "Results are not invented", right: "Don't make up data" }
          ],
          successtip: "✓ LAB SAFETY!\nThese rules are universal in every science lab worldwide! Safety first! 🥽"
        },
        {
          type: "mc",
          q: "Complete: 'Safety goggles _____ removed during experiments.'",
          options: [
            "is not",
            "are not",
            "not are"
          ],
          a: "are not",
          tip: "Plural goggles = ARE NOT!",
          successtip: "✓ NEVER REMOVE!\nSafety equipment is NEVER removed during active experiments! Protect your eyes! 👀"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los químicos no se mezclan sin supervisión'",
          words: ["Chemicals", "not", "mixed", "supervision", "without", "are"],
          a: "Chemicals are not mixed without supervision",
          extra: ["is"],
          tip: "Without supervision = sin supervisión!",
          successtip: "✓ SUPERVISION REQUIRED!\n'Without supervision' = key safety phrase. Never work alone with dangerous substances! ⚠️"
        },
        {
          type: "input",
          q: "Complete: 'The equipment _____ after use.' (clean - negative)",
          a: "is not cleaned",
          alts: ["isn't cleaned"],
          tip: "Wait, this is bad! Equipment SHOULD be cleaned!",
          successtip: "✓ POOR LAB PRACTICE!\nThis describes a problem! Equipment MUST be cleaned after use! Always! 🧼"
        },
        {
          type: "match",
          q: "Match what is NOT done in a good lab:",
          matches: [
            { left: "Data is not fabricated", right: "Results must be real" },
            { left: "Chemicals are not wasted", right: "Use only what's needed" },
            { left: "Equipment is not misused", right: "Use tools correctly" }
          ],
          successtip: "✓ GOOD SCIENCE!\nHonesty, efficiency, proper technique - foundations of good scientific practice! 🏆"
        },
        {
          type: "mc",
          q: "'The liquid is not removed from heat.' When does it stay on heat?",
          options: [
            "Until the experiment ends",
            "For 5 minutes only",
            "It depends on the procedure"
          ],
          a: "It depends on the procedure",
          successtip: "✓ FOLLOW PROCEDURES!\nEvery experiment has specific instructions. Always follow them exactly! 📋"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los resultados no se alteran'",
          words: ["results", "not", "are", "The", "altered"],
          a: "The results are not altered",
          extra: ["is", "alter"],
          tip: "Altered = cambiados/modificados!",
          successtip: "✓ DATA INTEGRITY!\nAltering results is scientific fraud! Real scientists NEVER change data! 📊"
        }
      ]
    },

    // SET 7
    {
      id: "m9-u8-s7",
      title: "Set 7: Advanced Vocabulary - Chemistry ⚗️",
      context: `**Chemistry Lab**

Professor: "Now we work with chemical reactions. Watch how substances dissolve, react, and transform..."`,
      
      questions: [
        {
          type: "mc",
          q: "What happens when salt is put in water?",
          options: [
            "It is dissolved",
            "It is melted",
            "It is frozen"
          ],
          a: "It is dissolved",
          tip: "DISSOLVE = disappear into the liquid!",
          successtip: "✓ DISSOLVING!\nDISSOLVE ≠ MELT! Dissolve = disappear in liquid. Melt = solid becomes liquid from heat! 🧂"
        },
        {
          type: "traduce",
          q: "Traduce: 'La sustancia se disuelve completamente'",
          words: ["substance", "completely", "The", "dissolved", "is"],
          a: "The substance is dissolved completely",
          extra: ["are", "dissolves"],
          successtip: "✓ COMPLETE DISSOLUTION!\n'Completely', 'fully', 'entirely' - adverbs showing total transformation! 💯"
        },
        {
          type: "input",
          q: "Complete: 'The chemicals _____ to create a reaction.' (combine)",
          a: "are combined",
          alts: ["are mixed"],
          tip: "COMBINE = put together for a specific purpose!",
          successtip: "✓ COMBINING!\nCOMBINE is more precise than MIX in chemistry - it implies interaction! ⚗️"
        },
        {
          type: "match",
          q: "Match the chemical process:",
          matches: [
            { left: "Dissolve", right: "Solid disappears in liquid" },
            { left: "Evaporate", right: "Liquid becomes gas" },
            { left: "Condense", right: "Gas becomes liquid" }
          ],
          successtip: "✓ STATE CHANGES!\nDissolution and phase changes - fundamental chemistry concepts! 🔄"
        },
        {
          type: "mc",
          q: "Complete: 'When heated, the liquid _____ into gas.'",
          options: [
            "evaporates",
            "is evaporated",
            "are evaporated"
          ],
          a: "is evaporated",
          tip: "Use passive to emphasize the transformation!",
          successtip: "✓ EVAPORATION!\nBoth active 'evaporates' and passive 'is evaporated' work! Passive sounds more scientific! 💨"
        },
        {
          type: "traduce",
          q: "Traduce: 'La reacción se observa bajo el microscopio'",
          words: ["reaction", "the", "The", "observed", "is", "microscope", "under"],
          a: "The reaction is observed under the microscope",
          extra: ["are"],
          successtip: "✓ MICROSCOPIC OBSERVATION!\n'Under the microscope' = bajo el microscopio. Essential in biology and chemistry! 🔬"
        },
        {
          type: "input",
          q: "Complete: 'Gas _____ when the mixture is heated.' (release)",
          a: "is released",
          alts: ["gets released"],
          tip: "RELEASE = liberar/soltar!",
          successtip: "✓ GAS RELEASE!\nMany chemical reactions release gas! 'Is released', 'is produced', 'is formed'! 💨"
        },
        {
          type: "match",
          q: "Match the observation with the process:",
          matches: [
            { left: "Bubbles appear", right: "Gas is released" },
            { left: "Color changes", right: "Reaction is occurring" },
            { left: "Heat is felt", right: "Energy is released" }
          ],
          successtip: "✓ REACTION INDICATORS!\nThese signs tell you a chemical reaction is happening! Classic chemistry! 🌡️"
        },
        {
          type: "mc",
          q: "What does 'The solution is stirred constantly' mean?",
          options: [
            "It's mixed all the time",
            "It's mixed once",
            "It's never mixed"
          ],
          a: "It's mixed all the time",
          tip: "CONSTANTLY = all the time, non-stop!",
          successtip: "✓ CONSTANT STIRRING!\n'Constantly', 'continuously', 'without stopping' - show ongoing action! 🔄"
        },
        {
          type: "traduce",
          q: "Traduce: 'Las partículas se separan durante el proceso'",
          words: ["particles", "the", "are", "during", "The", "separated", "process"],
          a: "The particles are separated during the process",
          extra: ["is"],
          successtip: "✓ SEPARATION!\nSeparating particles is key in chemistry! Filtration, distillation, centrifugation! ⚛️"
        }
      ]
    },

    // SET 8
    {
      id: "m9-u8-s8",
      title: "Set 8: Questions in Passive Voice ❓",
      context: `**Lab Quiz**

Teaching Assistant: "Let's test your understanding. I'll ask questions about the procedures..."`,
      
      questions: [
        {
          type: "mc",
          q: "Question form: '_____ the water heated before adding salt?'",
          options: [
            "Is",
            "Does",
            "Are"
          ],
          a: "Is",
          tip: "Question passive: IS/ARE + subject + past participle?",
          successtip: "✓ PASSIVE QUESTIONS!\nPattern: IS/ARE + subject + past participle? Works for any passive question! ❓"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Se registran los resultados diariamente?'",
          words: ["the", "recorded", "Are", "daily", "results"],
          a: "Are the results recorded daily",
          extra: ["Is", "record"],
          tip: "Plural results = ARE!",
          successtip: "✓ DAILY RECORDING!\n'Daily', 'weekly', 'monthly' - frequency words work great in questions! 📅"
        },
        {
          type: "input",
          q: "Complete: '_____ the thermometer placed correctly?' (question!)",
          a: "Is",
          alts: ["Was"],
          tip: "Singular thermometer = IS!",
          successtip: "✓ YES/NO QUESTIONS!\nIS/ARE at the beginning = yes/no question. Simple pattern! ✅"
        },
        {
          type: "match",
          q: "Match the question with its answer:",
          matches: [
            { left: "Is the solution heated?", right: "Yes, it is" },
            { left: "Are the results recorded?", right: "Yes, they are" },
            { left: "Is the experiment repeated?", right: "Yes, it is" }
          ],
          successtip: "✓ SHORT ANSWERS!\nYes, it is / Yes, they are. Short answers use the same auxiliary! 💬"
        },
        {
          type: "mc",
          q: "Complete the question: '_____ the chemicals mixed properly?'",
          options: [
            "Is",
            "Are",
            "Does"
          ],
          a: "Are",
          tip: "Plural chemicals = ARE!",
          successtip: "✓ PLURAL QUESTIONS!\nPlural subjects always use ARE in questions too! 🧪🧪"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Se usa el microscopio para este experimento?'",
          words: ["microscope", "the", "experiment", "for", "Is", "this", "used"],
          a: "Is the microscope used for this experiment",
          extra: ["Are"],
          tip: "'For this experiment' = para este experimento!",
          successtip: "✓ PURPOSE QUESTIONS!\n'Is X used for Y?' - great pattern for asking about purposes! 🎯"
        },
        {
          type: "input",
          q: "Complete: 'How often _____ the temperature checked?' (question)",
          a: "is",
          alts: ["is the temperature"],
          tip: "Pattern: How often + IS + subject + past participle?",
          successtip: "✓ FREQUENCY QUESTIONS!\n'How often is it checked?' Every minute? Every hour? Key lab question! ⏱️"
        },
        {
          type: "match",
          q: "Match the question type:",
          matches: [
            { left: "Yes/No question", right: "Is the water boiled?" },
            { left: "Frequency question", right: "How often is it measured?" },
            { left: "Method question", right: "How is it prepared?" }
          ],
          successtip: "✓ QUESTION TYPES!\nYes/No, How often, How - different question types for different information! ❓"
        },
        {
          type: "mc",
          q: "Negative question: '_____ the solution stirred before heating?'",
          options: [
            "Isn't",
            "Doesn't",
            "Aren't"
          ],
          a: "Isn't",
          tip: "Singular solution = ISN'T!",
          successtip: "✓ NEGATIVE QUESTIONS!\nISN'T/AREN'T + subject + past participle? Used to check or confirm! ✅"
        },
        {
          type: "traduce",
          q: "Traduce: '¿Por qué se repite el experimento?'",
          words: ["the", "Why", "repeated", "experiment", "is"],
          a: "Why is the experiment repeated",
          extra: ["are"],
          tip: "'Why' questions ask for reasons!",
          successtip: "✓ WHY QUESTIONS!\n'Why is it repeated?' To verify results! Good science requires repetition! 🔁"
        }
      ]
    },

    // SET 9
    {
      id: "m9-u8-s9",
      title: "Set 9: Real-World Applications 🏭",
      context: `**Industrial Chemistry**

Factory Tour Guide: "In industry, we use the same passive descriptions for large-scale processes. Let me show you..."`,
      
      questions: [
        {
          type: "mc",
          q: "In factories, why use passive voice?",
          options: [
            "To focus on the product, not workers",
            "To sound complicated",
            "To confuse people"
          ],
          a: "To focus on the product, not workers",
          tip: "Passive emphasizes WHAT happens, not WHO does it!",
          successtip: "✓ INDUSTRIAL FOCUS!\nFactories care about the PRODUCT and PROCESS, not individual workers! 🏭"
        },
        {
          type: "traduce",
          q: "Traduce: 'El producto se fabrica en esta planta'",
          words: ["The", "manufactured", "is", "this", "product", "in", "plant"],
          a: "The product is manufactured in this plant",
          extra: ["are"],
          successtip: "✓ MANUFACTURING!\nMANUFACTURE = fabricar. High-level word for industrial production! 🏭"
        },
        {
          type: "input",
          q: "Complete: 'The materials _____ from different suppliers.' (source)",
          a: "are sourced",
          alts: ["are obtained", "are purchased"],
          tip: "SOURCE (verb) = obtain from a particular place!",
          successtip: "✓ SOURCING!\nSOURCE as a verb is business English! 'Materials are sourced' = materials come from! 📦"
        },
        {
          type: "match",
          q: "Match the industrial process:",
          matches: [
            { left: "Raw materials", right: "Are received and stored" },
            { left: "Products", right: "Are manufactured and tested" },
            { left: "Finished goods", right: "Are packaged and shipped" }
          ],
          successtip: "✓ PRODUCTION CHAIN!\nReceive → Manufacture → Test → Package → Ship. Universal industrial process! 🚚"
        },
        {
          type: "mc",
          q: "Complete: 'Quality _____ at every stage.'",
          options: [
            "is checked",
            "checks",
            "are checked"
          ],
          a: "is checked",
          tip: "Quality (singular) = IS!",
          successtip: "✓ QUALITY CONTROL!\nEvery factory has quality checks! 'Is checked', 'is tested', 'is verified'! ✅"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los productos se empaquetan automáticamente'",
          words: ["packaged", "The", "automatically", "are", "products"],
          a: "The products are packaged automatically",
          extra: ["is"],
          tip: "Automatically = automáticamente!",
          successtip: "✓ AUTOMATION!\nModern factories use automation! 'Automatically' = sin intervención humana! 🤖"
        },
        {
          type: "input",
          q: "Complete: 'Defective items _____ before shipping.' (remove)",
          a: "are removed",
          alts: ["are eliminated", "are discarded"],
          tip: "DEFECTIVE = defectuoso/con fallas!",
          successtip: "✓ QUALITY ASSURANCE!\nDefective = faulty, damaged, imperfect. Must be removed! 🚫"
        },
        {
          type: "match",
          q: "Match the quality step:",
          matches: [
            { left: "Products are tested", right: "Check if they work" },
            { left: "Defects are identified", right: "Find problems" },
            { left: "Standards are maintained", right: "Keep quality high" }
          ],
          successtip: "✓ QUALITY PROCESS!\nTest → Identify → Maintain. Continuous quality improvement! 📈"
        },
        {
          type: "mc",
          q: "'The goods are shipped worldwide.' What does this mean?",
          options: [
            "Products go to many countries",
            "Products stay in one place",
            "Products are made everywhere"
          ],
          a: "Products go to many countries",
          tip: "WORLDWIDE = all over the world!",
          successtip: "✓ GLOBAL TRADE!\nWORLDWIDE, GLOBALLY, INTERNATIONALLY - modern business is global! 🌍"
        },
        {
          type: "traduce",
          q: "Traduce: 'Las especificaciones se siguen estrictamente'",
          words: ["specifications", "strictly", "The", "followed", "are"],
          a: "The specifications are followed strictly",
          extra: ["is"],
          tip: "STRICTLY = estrictamente, rigurosamente!",
          successtip: "✓ SPECIFICATIONS!\nSPECS (specifications) must be followed exactly in manufacturing! No deviations! 📋"
        }
      ]
    },

    // SET 10
    {
      id: "m9-u8-s10",
      title: "Set 10: Instructions & Manuals 📖",
      context: `**Writing Lab Manuals**

Technical Writer: "When writing procedures, we use passive voice for clarity and professionalism. Let me show you the technique..."`,
      
      questions: [
        {
          type: "mc",
          q: "Why are manuals written in passive voice?",
          options: [
            "To focus on actions, not people",
            "To make it harder to read",
            "To use more words"
          ],
          a: "To focus on actions, not people",
          tip: "Manuals describe WHAT to do, not WHO does it!",
          successtip: "✓ TECHNICAL WRITING!\nPassive voice makes instructions universal - anyone can follow them! 📘"
        },
        {
          type: "traduce",
          q: "Traduce: 'Los pasos se describen detalladamente'",
          words: ["The", "are", "steps", "in", "described", "detail"],
          a: "The steps are described in detail",
          extra: ["is"],
          tip: "'In detail' = con detalle!",
          successtip: "✓ DETAILED DESCRIPTIONS!\n'In detail', 'clearly', 'precisely' - good manuals are thorough! 📝"
        },
        {
          type: "input",
          q: "Complete: 'Safety precautions _____ before starting.' (list)",
          a: "are listed",
          alts: ["are provided", "are given"],
          tip: "Plural precautions = ARE!",
          successtip: "✓ SAFETY FIRST!\nEvery good manual starts with safety! 'Are listed', 'are explained', 'are outlined'! ⚠️"
        },
        {
          type: "match",
          q: "Match the manual section with its passive form:",
          matches: [
            { left: "Introduction", right: "Purpose is explained" },
            { left: "Procedure", right: "Steps are described" },
            { left: "Results", right: "Outcomes are documented" }
          ],
          successtip: "✓ MANUAL STRUCTURE!\nIntro → Procedure → Results. Standard format for lab manuals worldwide! 📚"
        },
        {
          type: "mc",
          q: "Complete: 'Diagrams _____ for clarity.'",
          options: [
            "is included",
            "are included",
            "includes"
          ],
          a: "are included",
          tip: "Plural diagrams = ARE!",
          successtip: "✓ VISUAL AIDS!\nDiagrams, charts, photos - visual elements make manuals clearer! 📊"
        },
        {
          type: "traduce",
          q: "Traduce: 'Cada paso se ilustra con imágenes'",
          words: ["step", "illustrated", "is", "Each", "images", "with"],
          a: "Each step is illustrated with images",
          extra: ["are"],
          tip: "Each step (singular) = IS!",
          successtip: "✓ ILLUSTRATED GUIDES!\n'Illustrated with images' = con imágenes. Pictures help understanding! 🖼️"
        },
        {
          type: "input",
          q: "Complete: 'Warnings _____ in bold text.' (highlight)",
          a: "are highlighted",
          alts: ["are shown", "are displayed"],
          tip: "BOLD = negrita, in bold = en negrita!",
          successtip: "✓ EMPHASIS!\nWarnings in BOLD, CAPITALS, or RED - visual emphasis for safety! 🚨"
        },
        {
          type: "match",
          q: "Match the formatting with its purpose:",
          matches: [
            { left: "Bold text", right: "Important information is emphasized" },
            { left: "Numbered lists", right: "Steps are organized in order" },
            { left: "Color coding", right: "Categories are distinguished" }
          ],
          successtip: "✓ DOCUMENT DESIGN!\nGood formatting makes complex procedures easy to follow! 🎨"
        },
        {
          type: "mc",
          q: "'Measurements are given in metric units.' What does this mean?",
          options: [
            "Numbers use meters, liters, grams",
            "Numbers are very large",
            "Numbers are in feet and inches"
          ],
          a: "Numbers use meters, liters, grams",
          tip: "METRIC = sistema métrico!",
          successtip: "✓ METRIC SYSTEM!\nScience uses metric worldwide! Meters, liters, grams, Celsius! 🌡️"
        },
        {
          type: "traduce",
          q: "Traduce: 'Las referencias se incluyen al final'",
          words: ["end", "at", "References", "included", "the", "are"],
          a: "References are included at the end",
          extra: ["is"],
          tip: "'At the end' = al final!",
          successtip: "✓ CITATIONS!\nReferences, bibliography, sources - essential in scientific writing! Good work cites sources! 📚"
        }
      ]
    }
  ],

  // READING PRACTICE
  readingSet: {
    id: "m9-u8-reading",
    title: "Reading Practice - The Water Purification Process",
    category: "reading",
    
    questions: [
      {
        type: "passage",
        passage: `**The Water Purification Process**

**Step 1: Collection**

Water is collected from natural sources such as rivers, lakes, or underground wells. The raw water is pumped into large holding tanks where initial screening is performed. Large debris like leaves and sticks are removed at this stage.`
      },
      {
        type: "mc",
        q: "Where is water collected from?",
        options: [
          "Only from rivers",
          "From natural sources like rivers and lakes",
          "From the ocean"
        ],
        a: "From natural sources like rivers and lakes",
        successtip: "✓ WATER SOURCES!\nMost drinking water comes from fresh water sources - rivers, lakes, underground! 💧"
      },
      {
        type: "mc",
        q: "What is removed during initial screening?",
        options: [
          "Chemicals",
          "Large debris like leaves",
          "Bacteria"
        ],
        a: "Large debris like leaves",
        successtip: "✓ SCREENING!\nFirst step removes BIG stuff! Later steps remove chemicals and bacteria! 🍃"
      },
      {
        type: "passage",
        passage: `**Step 2: Coagulation and Sedimentation**

After screening, chemicals called coagulants are added to the water. These chemicals cause small particles to stick together and form larger clumps. The water is then moved to sedimentation tanks where these clumps, called "floc," are allowed to settle to the bottom. The clear water on top is carefully removed.`
      },
      {
        type: "mc",
        q: "What are coagulants used for?",
        options: [
          "To make water hot",
          "To make small particles stick together",
          "To add flavor to water"
        ],
        a: "To make small particles stick together",
        tip: "Coagulants make particles clump together!",
        successtip: "✓ COAGULATION!\nCOAGULATE = cause to clump together. Key step in water treatment! ⚗️"
      },
      {
        type: "input",
        q: "What are the larger clumps called?",
        a: "floc",
        alts: ["Floc"],
        successtip: "✓ FLOC!\nTechnical term in water treatment! Floc settles to the bottom by gravity! 📉"
      },
      {
        type: "passage",
        passage: `**Step 3: Filtration**

The clear water is passed through filters made of sand, gravel, and charcoal. These filters remove remaining particles, bacteria, and some chemicals. Multiple layers are used to ensure thorough cleaning. The water becomes even clearer after this stage.`
      },
      {
        type: "mc",
        q: "What materials are the filters made of?",
        options: [
          "Plastic and metal",
          "Sand, gravel, and charcoal",
          "Paper and cloth"
        ],
        a: "Sand, gravel, and charcoal",
        successtip: "✓ NATURAL FILTERS!\nSand and gravel have been used for water filtration for thousands of years! 🏜️"
      },
      {
        type: "input",
        q: "Complete: 'Multiple layers _____ to ensure thorough cleaning.' (use)",
        a: "are used",
        tip: "Plural layers = ARE + past participle!",
        successtip: "✓ LAYERED FILTRATION!\nEach layer catches different sizes of particles! Smart design! 🎯"
      },
      {
        type: "passage",
        passage: `**Step 4: Disinfection and Storage**

Finally, the filtered water is disinfected to kill any remaining bacteria and viruses. Chlorine or other disinfectants are added in carefully measured amounts. The clean water is then stored in covered reservoirs until it is distributed to homes and businesses through a network of pipes.`
      },
      {
        type: "mc",
        q: "What is added to kill bacteria and viruses?",
        options: [
          "Sugar",
          "Salt",
          "Chlorine or disinfectants"
        ],
        a: "Chlorine or disinfectants",
        successtip: "✓ DISINFECTION!\nChlorine kills harmful microorganisms! That's why pool water smells like chlorine! 🏊"
      },
      {
        type: "mc",
        q: "Where is clean water stored before distribution?",
        options: [
          "In open ponds",
          "In covered reservoirs",
          "In plastic bottles"
        ],
        a: "In covered reservoirs",
        tip: "COVERED = protected from contamination!",
        successtip: "✓ SAFE STORAGE!\nCovered reservoirs protect clean water from dirt, animals, and rain! 🏗️"
      },
      {
        type: "input",
        q: "How is water delivered to homes? (2 words)",
        a: "through pipes",
        alts: ["via pipes", "using pipes", "by pipes"],
        successtip: "✓ DISTRIBUTION NETWORK!\nPipe networks carry water to millions of homes! Underground infrastructure! 🚰"
      }
    ]
  },

  // REVIEW PRACTICE
  reviewSet: {
    id: "m9-u8-review",
    title: "Unit Review - Passive Voice Mastery",
    category: "review",
    
    exercises: [
      {
        type: "mc",
        q: "Choose the correct passive form: 'The experiment _____ three times.'",
        options: [
          "repeats",
          "is repeated",
          "are repeated"
        ],
        a: "is repeated",
        successtip: "✓ REPETITION!\nSingular experiment = IS repeated! Remember: repetition validates results! 🔁"
      },
      {
        type: "traduce",
        q: "Traduce: 'Los químicos se mezclan cuidadosamente en el laboratorio'",
        words: ["chemicals", "mixed", "The", "are", "in", "carefully", "laboratory", "the"],
        a: "The chemicals are mixed carefully in the laboratory",
        extra: ["is"],
        successtip: "✓ COMPLETE SENTENCE!\nSubject + ARE + past participle + adverb + location. Perfect structure! 🎯"
      },
      {
        type: "match",
        q: "Match the process with its passive description:",
        matches: [
          { left: "Heating water to 100°C", right: "The water is boiled" },
          { left: "Writing down results", right: "The results are recorded" },
          { left: "Looking at cells", right: "The cells are observed" }
        ],
        successtip: "✓ PROCESS MASTERY!\nYou can now describe any lab process using passive voice! 🏆"
      },
      {
        type: "input",
        q: "Complete the negative: 'The solution _____ during the reaction.' (stir - negative)",
        a: "is not stirred",
        alts: ["isn't stirred"],
        successtip: "✓ NEGATIVE PASSIVE!\nIS NOT + past participle. You've mastered this pattern! 🚫"
      },
      {
        type: "mc",
        q: "Which is the correct question? '_____ the temperature recorded?'",
        options: [
          "Does",
          "Is",
          "Do"
        ],
        a: "Is",
        successtip: "✓ PASSIVE QUESTIONS!\nIS/ARE at the start = passive question. Simple and professional! ❓"
      },
      {
        type: "traduce",
        q: "Traduce: '¿Se usan termómetros en todos los experimentos?'",
        words: ["thermometers", "Are", "experiments", "all", "in", "used"],
        a: "Are thermometers used in all experiments",
        extra: ["Is"],
        successtip: "✓ EQUIPMENT QUESTIONS!\nPlural thermometers = ARE. Question about standard procedures! 🌡️"
      },
      {
        type: "input",
        q: "Industrial context: 'Quality _____ at every stage.' (check)",
        a: "is checked",
        alts: ["is verified", "is tested"],
        successtip: "✓ QUALITY CONTROL!\nYou can now use passive in professional contexts! Industry-ready! 🏭"
      },
      {
        type: "match",
        q: "Match the context with the passive use:",
        matches: [
          { left: "Science lab", right: "Results are recorded" },
          { left: "Factory", right: "Products are manufactured" },
          { left: "Manual", right: "Steps are described" }
        ],
        successtip: "✓ VERSATILE SKILL!\nPassive voice works in labs, factories, manuals - everywhere! 🌟"
      },
      {
        type: "mc",
        q: "Complete the advanced sentence: 'The data _____ and analyzed before conclusions are drawn.'",
        options: [
          "is collected",
          "collects",
          "are collect"
        ],
        a: "is collected",
        successtip: "✓ COMPLEX SENTENCES!\nYou can now combine multiple passive verbs! Advanced level! 📊"
      },
      {
        type: "mc",
        q: "🎉 UNIT COMPLETE! 🎉\n\nWhat's the main benefit of using passive voice in scientific writing?",
        options: [
          "It focuses on the actions and results, not who does them",
          "It makes sentences longer",
          "It sounds more complicated"
        ],
        a: "It focuses on the actions and results, not who does them",
        successtip: "🏆✨ AMAZING WORK! ✨🏆\n\nYou've mastered passive voice from B1 to B2! You can now:\n✅ Describe scientific processes professionally\n✅ Write lab procedures and manuals\n✅ Use passive in questions and negatives\n✅ Apply it in real-world contexts\n\nThis skill is essential for:\n🔬 Science and research\n🏭 Industry and manufacturing\n📚 Academic writing\n💼 Professional communication\n\nYou're speaking like a professional English speaker! Keep going! 🚀🌟"
      }
    ]
  }
};
